import React, { useEffect, useMemo, useState } from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import moment from 'moment-timezone';
import MiscUtils from 'services/MiscUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import CPPUtils from 'services/CPPUtils';
import CalendarUtils from 'services/CalendarUtils';

const FORMAT = 'YYYY/MM/DD';
const NAME = 'Last Period';

// Export encode/decode functions for use in CPPListElement
export const PregnancyCalculatorUtils = {
  NAME,
  FORMAT,

  /**
   * Decode a Last Period note into display format for CPP
   * @param {object} item - The health record item
   * @returns {object|null} - { text, notes } or null if shouldn't display
   */
  decodeForDisplay(item) {
    let dateStr = item.history?.find(({ value }) => {
      let split = value.notes?.split('/');
      return split?.length === 3;
    })?.value?.notes;

    if (!dateStr) return null;

    let date = moment(dateStr, FORMAT);
    let diff = moment().diff(date, 'weeks');

    // Don't show if more than 43 weeks
    if (diff > 43) return null;

    let weeks = CPPUtils.calculateWeeksPregnant(date.unix());
    let dueDate = CalendarUtils.format(CPPUtils.calculateDueDate(date.unix()), FORMAT);
    let lmpStr = CalendarUtils.format(date.unix(), FORMAT);

    return {
      text: `Gestational Age ${weeks}`,
      notes: (
        <span>
          LMP {lmpStr}
          <br />
          EDD {dueDate}
        </span>
      ),
    };
  },
};

const PregnancyCalculatorModal = ({ patient, onSubmit }) => {
  const dispatch = useAppDispatch();

  const patientHr = useAppSelector((state) => state.healthRecords.byId[patient.id]);

  const previousLmp = useMemo(() => {
    const notes = patientHr?.healthRecords?.notes ?? [];
    let item = notes.find(
      (note) => note.value.name === NAME && note.history[0].action !== 'Removed',
    );
    return item?.history?.find(({ value }) => {
      let split = value.notes?.split('/');
      return split?.length === 3;
    })?.value?.notes;
  }, [patientHr]);

  const previousDate = previousLmp ? moment(previousLmp, FORMAT).unix() : moment().unix();

  const [lastPeriod, setLastPeriod] = useState(previousDate);
  const [dueDate, setDueDate] = useState();
  const [saving, setSaving] = useState(false);

  async function save() {
    let data = { value: { name: NAME, notes: moment.unix(lastPeriod).format(FORMAT) } };

    if (previousLmp) data.id = previousLmp.id;

    setSaving(true);
    await dispatch(
      api.HealthRecords.addOrEditHealthRecordPatientIssue({
        patientId: patient.id,
        field: 'notes',
        patientIssues: [data],
      }),
      {
        errorTitle: 'Last Period',
      },
    );
    setSaving(false);
  }

  function onChangeDueDate(date) {
    if (date) {
      setDueDate(date);
      let momentDate = moment.unix(date);
      let lastPeriod = momentDate.add(-280, 'days');
      setLastPeriod(lastPeriod.unix());
    }
  }

  useEffect(() => {
    let dueDate = CPPUtils.calculateDueDate(lastPeriod);
    setDueDate(dueDate);
  }, [lastPeriod]);

  function onChangeLastPeriod(date) {
    setLastPeriod(date);
  }

  const weeks = useMemo(() => {
    if (lastPeriod) {
      return CPPUtils.calculateWeeksPregnant(lastPeriod);
    }
    return undefined;
  }, [lastPeriod]);

  function getResult() {
    let result = '';
    if (dueDate) result += `Due date: ${moment.unix(dueDate).format(FORMAT)}\n`;

    if (weeks) result += `Weeks of pregnancy: ${weeks}\n`;
    return result;
  }

  async function copyToCliboard() {
    const result = getResult();

    if (result) {
      await navigator.clipboard.writeText(result);
      MiscUtils.showToastrInfo({
        title: 'Pregnancy Calculator',
        message: 'Values copied successfully!',
      });
    }
  }

  return (
    <div className="modal" style={{ width: '300px', height: '100%' }}>
      <div className="title">Pregnancy Calculator</div>
      <div className="padded-box">
        <div className="row">
          <DefaultDatePicker
            label="Date of Last Period (LMP)"
            autoFocus
            name="date"
            value={lastPeriod}
            onChange={(event, date) => onChangeLastPeriod(date)}
            style={{ width: '100%' }}
          />
          <div className="row">
            <DefaultTextField
              name="weeks"
              label="Weeks Pregnant"
              value={weeks || ''}
              disabled={true}
              style={{ width: 'calc(50% - 10px)', marginRight: '20px' }}
            />
            <DefaultDatePicker
              key={dueDate}
              label="Due Date (EDD)"
              name="due_date"
              value={dueDate}
              onChange={(event, date) => onChangeDueDate(date)}
              style={{ width: 'calc(50% - 10px)' }}
            />
          </div>
        </div>
        <DefaultButton
          name={onSubmit ? 'Submit' : 'Copy to Clipboard'}
          onClick={
            onSubmit
              ? () => {
                  onSubmit(getResult());
                }
              : copyToCliboard
          }
          style={{ width: '100%', marginTop: '30px' }}
          disabled={!weeks && !dueDate}
        />
        <DefaultButton
          name="Save Date of Last Period"
          className="blue"
          onClick={save}
          style={{ width: '100%', marginTop: '10px' }}
          disabled={!weeks && !dueDate}
          loading={saving}
        />
      </div>
    </div>
  );
};

export default PregnancyCalculatorModal;
